import { type CSSProperties, type JSX, type ReactNode } from "react";
import { cn } from "./lib/utils";
import { NavIcon } from "./NavIcon";
import { Tooltip, TooltipContent, TooltipTrigger } from "./ui/tooltip";

/**
 * Accent slot -> token-backed tile class. Written out literally because Tailwind
 * only emits classes it can see as static strings in the source.
 */
/**
 * Accent slot -> token-backed classes. Written out literally because Tailwind
 * only emits classes it can see as static strings in the source.
 */
const accentTextClasses: Record<number, string> = {
  1: "text-app-accent-1",
  2: "text-app-accent-2",
  3: "text-app-accent-3",
  4: "text-app-accent-4",
  5: "text-app-accent-5",
  6: "text-app-accent-6"
};

/** A 10% wash of the app's own accent, used as the row hover highlight. */
const accentHoverClasses: Record<number, string> = {
  1: "hover:bg-app-accent-1/10",
  2: "hover:bg-app-accent-2/10",
  3: "hover:bg-app-accent-3/10",
  4: "hover:bg-app-accent-4/10",
  5: "hover:bg-app-accent-5/10",
  6: "hover:bg-app-accent-6/10"
};

export interface AppEntryProps {
  id: string;
  label: string;
  /** Image URL or data URL from the app's registry entry. */
  icon: string;
  /** Normalized #RRGGBB application color from the runtime registry. */
  color?: string;
  /** Token-backed accent used only by disabled workspace mockup apps. */
  accent?: number;
  href?: string;
  route?: string;
  active: boolean;
  collapsed: boolean;
  /** True for workspace-mockup placeholder apps that have no real route. */
  disabled?: boolean;
  onExpand: (appId: string) => void;
}

/** One app's row in the sidebar: an icon tile carrying the app's own registry image, plus wordmark. */
export function AppEntry({
  id,
  label,
  icon,
  color,
  accent,
  href,
  route,
  active,
  collapsed,
  disabled = false,
  onExpand
}: AppEntryProps): JSX.Element {
  const accentText = accent ? (accentTextClasses[accent] ?? accentTextClasses[1]) : "text-sidebar-text";
  const appColorStyle = color
    ? {
        color,
        "--app-entry-hover-color": `color-mix(in srgb, ${color} 10%, transparent)`
      } as CSSProperties
    : undefined;

  let stateClassName = "hover:bg-sidebar-text/10";
  if (accent) stateClassName = accentHoverClasses[accent] ?? accentHoverClasses[1];
  if (color) stateClassName = "hover:bg-[var(--app-entry-hover-color)]";
  if (disabled) {
    stateClassName = "cursor-not-allowed opacity-60";
  }

  const rowClassName = cn(
    "flex w-full items-center rounded-lg py-1 no-underline transition-[background-color,color,transform] duration-150 ease-out active:scale-[.99] motion-reduce:transition-none motion-reduce:active:scale-100",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
    "focus-visible:ring-offset-2 focus-visible:ring-offset-shell-canvas",
    collapsed ? "justify-center px-0" : "gap-3 px-1",
    stateClassName
  );

  const content: ReactNode = (
    <>
      {/* The accent lives on the icon and wordmark themselves — no filled tile —
          with a 10% wash of the same accent as the row highlight. The inline
          colour mirrors the wordmark span below: the accent class on this span
          would otherwise beat the row's inherited colour, and a currentColor
          glyph paints with whatever colour lands here. */}
      <span
        aria-hidden="true"
        className={cn(
          "flex size-8 shrink-0 items-center justify-center text-lg",
          disabled ? "text-sidebar-text-muted" : accentText
        )}
        style={disabled ? undefined : appColorStyle}
      >
        <NavIcon icon={icon} className="h-6 w-6 object-contain" />
      </span>
      {!collapsed && (
        <span
          className={cn(
            // Bebas Neue via font-brand: a display face that only reads right in
            // caps, and uppercase display type needs air rather than tight tracking.
            "truncate font-brand text-xl leading-none tracking-wide uppercase",
            disabled ? "text-sidebar-text-muted" : accentText
          )}
          style={disabled ? undefined : appColorStyle}
        >
          {label}
        </span>
      )}
    </>
  );

  const link =
    disabled || !href || !route ? (
      <span
        data-platform-app-switcher-link=""
        data-switcher-app-id={id}
        aria-disabled="true"
        className={rowClassName}
        style={disabled ? undefined : appColorStyle}
      >
        {content}
      </span>
    ) : (
      <a
        href={href}
        data-platform-app-switcher-link=""
        data-switcher-app-id={id}
        aria-current={active ? "true" : undefined}
        onClick={(event) => {
          if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) {
            return;
          }

          event.preventDefault();
          onExpand(id);
        }}
        className={rowClassName}
        style={appColorStyle}
      >
        {content}
      </a>
    );

  if (!collapsed) {
    return link;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>{link}</TooltipTrigger>
      <TooltipContent side="right">{label}</TooltipContent>
    </Tooltip>
  );
}
